import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/Button';
import { cn } from '../ui/utils';
import type { ReprocessMode } from '@/lib/api';

interface ReprocessDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentName: string;
  onConfirm: (mode: ReprocessMode) => void;
  isReprocessing: boolean;
}

const MODE_OPTIONS: { value: ReprocessMode; label: string; hint: string }[] = [
  {
    value: 'append',
    label: 'Append',
    hint: 'Keep the existing detections and add any new findings (no duplicates).',
  },
  {
    value: 'replace',
    label: 'Replace',
    hint: 'Drop the previous AI detections and keep only the new run.',
  },
];

export function ReprocessDocumentDialog({
  open,
  onOpenChange,
  documentName,
  onConfirm,
  isReprocessing,
}: ReprocessDocumentDialogProps) {
  const [mode, setMode] = useState<ReprocessMode>('append');
  const activeHint = MODE_OPTIONS.find((o) => o.value === mode)?.hint ?? '';

  // Each time the dialog opens, start from the default ('append') so a prior
  // one-off 'replace' choice doesn't silently carry into the next document.
  useEffect(() => {
    if (open) setMode('append');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#7035C4]" />
            Reprocess with manual flags
          </DialogTitle>
          <DialogDescription>
            This re-runs AI detection on '{documentName}', steered by the entities
            you manually flagged on this document. The detector uses your flags as
            examples to find similar entities it may have missed. Your manual flags
            are always kept, and new suggestions will appear when detection finishes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">When detection finishes</div>
          <div
            role="radiogroup"
            aria-label="Reprocess mode"
            className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50"
          >
            {MODE_OPTIONS.map((option) => {
              const selected = mode === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={isReprocessing}
                  onClick={() => setMode(option.value)}
                  className={cn(
                    'px-4 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50',
                    selected
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500">{activeHint}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isReprocessing}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(mode)}
            disabled={isReprocessing}
          >
            {isReprocessing ? 'Reprocessing...' : 'Reprocess'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
